import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { Popup, PopupEventType, PopupPosition } from '../popover/Popup';
import { Icon } from '../icon/Icon';
import { Button } from '../button/Button';
import { useRef } from 'react';
import { Mode, PopupIntentStyle } from '../common/props';
import { mcClose } from '@mc/react-ui-icons';
import { InverseBgProvider } from '../theme/InverseBg';
import { useInverseBg } from '../theme/useInverseBg';
import { PopupActions } from '../popover/reactjs-popup/types';

type Props = {
  /**
   * Pokud je true, tak se tooltip zobrazí až PO kliknutí na trigger, tooltip je zobrazen jako zavíratelný pomocí křížku.
   * Pokud je false, je tooltip zobrazen po najetí myši.
   */
  closable?: boolean;
  /**
   * Možnost překrytí na co má tooltip reagovat - překrývá funkčnost closable.
   */
  on?: PopupEventType | PopupEventType[];
  trigger?: React.JSX.Element | ((isOpen: boolean) => React.JSX.Element);
  className?: string;
  children:
    | React.ReactNode
    | ((props: { onClose: () => void }) => React.ReactNode);
  position?: PopupPosition | PopupPosition[];
  disabled?: boolean;
  mouseEnterDelay?: number;
  mouseLeaveDelay?: number;
  intent?: PopupIntentStyle;
  mode?: Mode;
  noStopPropagation?: boolean;
  noPreventDefault?: boolean;
  nested?: boolean;
};
const DEFAULT_POSITIONS: PopupPosition[] = [
  'top center',
  'bottom center',
  'right center',
  'left center',
];
/**
 * Popover - dropdown s nějakou nabídkou
 */
export const Tooltip: React.FC<Props> = ({
  className,
  children,
  intent = 'none',
  mode,
  disabled,
  closable,
  trigger,
  on,
  mouseEnterDelay = 250,
  mouseLeaveDelay = 100,
  position,
  nested,
  ...other
}) => {
  const ref = useRef<PopupActions | undefined>(undefined);
  const calculatedPosition = React.useMemo(() => {
    return position
      ? Array.isArray(position)
        ? [...position, ...DEFAULT_POSITIONS]
        : [position, ...DEFAULT_POSITIONS]
      : DEFAULT_POSITIONS;
  }, [position]);
  let content = undefined;
  if (children) {
    if (closable) {
      content = (
        <div className={Classes.TOOLTIP_CLOSABLE_WRAPPER}>
          <div className={Classes.TOOLTIP_CLOSABLE_BODY}>
            {typeof children === 'function'
              ? children({
                  onClose: () => {
                    ref.current?.close();
                  },
                })
              : children}
          </div>
          <div className={Classes.TOOLTIP_CLOSABLE_HEADER}>
            <Button
              noFocus
              onClick={() => {
                ref.current?.close();
              }}
              tabIndex={-1}
              style={'link'}
              icon={<Icon fixedWidth icon={mcClose} />}
            />
          </div>
        </div>
      );
    } else {
      content =
        typeof children === 'function'
          ? children({
              onClose: () => {
                ref.current?.close();
              },
            })
          : children;
    }
  }

  const renderTrigger = (isOpen: boolean): React.JSX.Element => {
    return typeof trigger === 'function' ? trigger(isOpen) : trigger || <></>;
  };

  return (
    <InverseBgProvider inverse={useInverseBg()}>
      <Popup
        ref={ref}
        {...other}
        intent={intent}
        mode={mode}
        arrow
        on={on ? on : closable ? undefined : 'hover'}
        mouseEnterDelay={mouseEnterDelay}
        mouseLeaveDelay={mouseLeaveDelay}
        className={classNames(className, Classes.TOOLTIP, {
          [Classes.TOOLTIP_DEFAULT]: !closable,
          [Classes.TOOLTIP_CLOSABLE]: closable,
        })}
        disabled={disabled}
        trigger={renderTrigger}
        position={calculatedPosition}
        nested={nested}
      >
        {content && (
          <InverseBgProvider
            inverse={
              intent === 'warning' ||
              intent === 'danger' ||
              intent === 'success'
            }
          >
            {content}
          </InverseBgProvider>
        )}
      </Popup>
    </InverseBgProvider>
  );
};
